import metadataIcon from '../../assets/metric-metadata-icon-green.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "../MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../../utils/dashboardUtils";
import IconTooltip from "./../IconTooltip";
import { formatValue } from '../../utils/helpers';

interface SingleBarProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    name: string;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const SingleBar = ({ metric, disaggData, name, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: SingleBarProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);

    // Destructure metric properties
    const { id, versions } = metric;
    const { value, unit } = versions[0];

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }

        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 50,
                right: window.innerWidth - tooltipRect.right < 200,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }
    }, []);

    return (
        <div className="mx-2 my-2">
            <div className="flex items-center justify-start w-full">
                <p className="text-lacey-text text-[11px] font-bold mr-2 w-24">{name}</p>
                <p className="text-lacey-text text-[11px] font-bold mr-2 w-10">{formatValue(value, unit, id)}</p>
                {/* Dynamic width bar for formatted value */}
                <div 
                    className="bg-lacey-green text-white text-center text-xs font-bold py-1 h-3"
                    style={{ width: `${value * 100}rem`, maxWidth: '300px' }}
                >
                </div>
                
                {showMetadataIcon && 
                    <div ref={tooltipRef} className="shrink-0 ml-2">
                        <button onClick={handleClick}>
                            <img src={metadataIcon} alt="Green chart icon" className="w-3 h-3"/>
                        </button>
                        <IconTooltip edgeProximity={edgeProximity}/>
                    </div>
                }
                {/* {unit_descriptor && <p className={`text-color-cai-gray text-xs/4`}>{unit_descriptor}</p>} */}
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default SingleBar;
